import React from 'react';
import { Button } from 'primereact/button';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CircularMetric } from './components/CircularMetric';
import { StatCard } from './components/StatCard';
import { CoachingActionPlanDashboardProps, SkillFrequency } from './types';
import './coachingActionPlanDashboard.scss';
import { SkillFrequencyChart } from './components/SkillFrequencyChart';
import { useGetTeamleadCoachingDashboardSearchQuery, useGetTeamleadCoachingDashboardSkillsSearchQuery } from './coachingActionPlanDashboardSlice';
import { selectCurrentEmpid } from '../../../pages/login/authSlice';
import { ACTIONPLAN } from '../../../constants';
import { getCoachingEffectivenessColor, getCoachingEffectivenessIcon, getCoachingEffectivenessTrailColor, getPercentageColor, getPercentageIcon, getPercentageTrailColor } from '../managerCoachingActionPlanDashboard/utils';

const SKILL_COLORS = [
    '#0E4587',
    '#1257AB',
    '#1669D0',
    '#267DE8',
    '#4B94EC',
    '#70AAF0',
    '#95C0F4',
    '#BAD6F8',
    '#C9E1FF',
];

export const CoachingActionPlanDashboard: React.FC<CoachingActionPlanDashboardProps> = ({
    inputmonth,
    inputyear,
}) => {
    const navigate = useNavigate();
    const loggedEmpNum = useSelector(selectCurrentEmpid);

    const { data: coachingDashboard, isLoading: isDashboardLoading } = useGetTeamleadCoachingDashboardSearchQuery({
        employeeNumber: loggedEmpNum,
        year: inputyear,
        month: inputmonth,
    }, { skip: !loggedEmpNum || !inputyear || !inputmonth });


    const { data: coachingDashboardSkills, isLoading: isSkillsLoading } = useGetTeamleadCoachingDashboardSkillsSearchQuery({
        employeeNumber: loggedEmpNum,
        year: inputyear,
        month: inputmonth,
    }, { skip: !loggedEmpNum || !inputyear || !inputmonth });

    const isCAPLoading = isDashboardLoading || isSkillsLoading;

    const skillData: SkillFrequency[] = (coachingDashboardSkills?.data ?? []).map(
        (item: any, index: number) => ({
            name: item.coaching_skill_name,
            count: item.session_count,
            percentage: parseFloat((item.session_pct * 100).toFixed(2)),
            color: SKILL_COLORS[index % SKILL_COLORS.length],
        })
    );

    const getTileValueFormatted = (code: string) => {
        const tile = coachingDashboard?.data?.find((item: any) => item.tile_code === code);
        if (!tile || tile.tile_value === null) return null;
        return tile.tile_format === 'PERCENTAGE'
            ? parseFloat((tile.tile_value * 100).toFixed(2))
            : tile.tile_value;
    };

    const compliance = getTileValueFormatted('COMPLIANCE_RATIO');
    const acknowledgement = getTileValueFormatted('ACKNOWLEDGEMENT_RATIO');
    const coachingEffectiveness = getTileValueFormatted('COACHING_EFFECTIVENESS');
    const sessionsCompleted = getTileValueFormatted('SESSIONS_COMPLETED');
    const coachingPlansCompleted = getTileValueFormatted('COACHING_PLANS_COMPLETED');
    const followUpsCompleted = getTileValueFormatted('FOLLOW_UPS_COMPLETED');
    const coachingPerAgent = getTileValueFormatted('COACHING_PER_AGENT_AVG');
    const openCoachingPlans = getTileValueFormatted('OPEN_COACHING_PLANS');
    const overdueSessions = getTileValueFormatted('OVERDUE_SESSIONS');

    const TILE_TOOLTIPS: Record<string, string> = {
        SESSIONS_COMPLETED: 'The total number of coaching sessions that were started during the selected month, including both initial sessions and follow-ups.',
        COACHING_PLANS_COMPLETED: 'The number of coaching action plans that were closed during the selected month.',
        FOLLOW_UPS_COMPLETED: 'The number of follow-up coaching sessions that were held during the selected month. Initial sessions are not counted here.',
        COACHING_PER_AGENT_AVG: 'The average number of coaching events per team member during the selected month. A coaching event includes sessions, completed plans, and recognitions.',
        OPEN_COACHING_PLANS: 'Coaching action plans that are currently still open. Includes plans started on or before the end of the selected month.',
        OVERDUE_SESSIONS: "Sessions on currently-open coaching plans where the follow-up date has passed without a new session being created. Reflects today's status, not the selected month.",
        COMPLIANCE_RATIO: 'The percentage of team members who received at least one coaching event during the selected month. A coaching event includes sessions, completed plans, and recognitions.',
        ACKNOWLEDGEMENT_RATIO: 'The percentage of coaching plans created during the selected month that the associate has acknowledged.',
        COACHING_EFFECTIVENESS: `The percentage of coaching sessions where the coached metric improved 7 days after the coaching date. Most reliable once 12 weeks of data have been collected. 
        Reflects the last 12 weeks as of today, not the selected month.`,
        SKILL_FREQUENCY: 'A breakdown of coaching sessions during the selected month, grouped by the skill being coached.',
    };

    const handleOpenActionPlan = () => {
        navigate(`${ACTIONPLAN}`, { replace: true });
    };

    return (
        <div className="cap-dashboard">
            {/* ── Header ── */}
            <div className="cap-dashboard__header">
                <h2 className="cap-dashboard__title">Coaching Action Plan</h2>
                <Button
                    label="Coaching Action Plan Menu"
                    icon="pi pi-arrow-circle-right"
                    className="btn-primary"
                    onClick={handleOpenActionPlan}
                />
            </div>

            {/* ── Circular Metrics Row ── */}
            <hr className="cap-circular-metric__divider" />
            <div className="cap-dashboard__circular-row">
                <CircularMetric label="Compliance" value={compliance} color={getPercentageColor(compliance) ?? '#2C3E50'} trailColor={getPercentageTrailColor(compliance)}
                icon={getPercentageIcon(compliance)} showPercentage isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.COMPLIANCE_RATIO} />
                <CircularMetric label="Acknowledgement" value={acknowledgement} color={getPercentageColor(acknowledgement) ?? '#2C3E50'} trailColor={getPercentageTrailColor(acknowledgement)}
                icon={getPercentageIcon(acknowledgement)} showPercentage isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.ACKNOWLEDGEMENT_RATIO} />
                <CircularMetric label="Coaching Effectiveness" value={coachingEffectiveness} color={getCoachingEffectivenessColor(coachingEffectiveness) ?? '#2C3E50'} trailColor={getCoachingEffectivenessTrailColor(coachingEffectiveness)}
                icon={getCoachingEffectivenessIcon(coachingEffectiveness)} showPercentage isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.COACHING_EFFECTIVENESS} />
            </div>

            {/* ── Stats + Chart Row ── */}
            <div className="cap-dashboard__bottom-row">
                {/* Stat Cards Grid */}
                <div className="cap-dashboard__stat-grid">
                    <StatCard label="Sessions Completed" value={sessionsCompleted} isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.SESSIONS_COMPLETED} />
                    <StatCard label="Coaching Plans Completed" value={coachingPlansCompleted} isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.COACHING_PLANS_COMPLETED} />
                    <StatCard label="Follow Ups Completed" value={followUpsCompleted} isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.FOLLOW_UPS_COMPLETED} />
                    <StatCard label="Coaching Per Agent" value={coachingPerAgent} isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.COACHING_PER_AGENT_AVG} />
                    <StatCard label="Open Coaching Plans" value={openCoachingPlans} isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.OPEN_COACHING_PLANS} />
                    <StatCard label="Overdue Sessions" value={overdueSessions} isLoading={isCAPLoading} overdue tooltip={TILE_TOOLTIPS.OVERDUE_SESSIONS} />
                </div>

                {/* Skill Frequency Pie Chart */}
                <SkillFrequencyChart data={skillData} isLoading={isCAPLoading} tooltip={TILE_TOOLTIPS.SKILL_FREQUENCY} />
            </div>
        </div>
    );
};

export default CoachingActionPlanDashboard;